import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ApiError, apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

// ── Types mirrored from server agentLoop.ts ─────────────────────────────────
type AgentEvent =
  | { kind: 'user'; text: string; at: string }
  | { kind: 'assistant'; text: string; at: string }
  | { kind: 'tool_call'; name: string; destructive: boolean; args: Record<string, unknown>; at: string; toolCallId: string }
  | { kind: 'tool_result'; toolCallId: string; name: string; resultPreview: string; at: string }
  | { kind: 'denied'; toolCallId: string; name: string; reason?: string; at: string }
  | { kind: 'stopped'; reason: string; at: string }
  // Visible confirmation that Neo's final answer was mirrored into the bound
  // Sentinel incident's comment thread (or skipped/failed, with a reason).
  | { kind: 'sentinel_comment'; status: 'posted' | 'skipped' | 'failed'; method?: 'arm' | 'graph'; reason?: string; at: string };

interface PendingDestructive {
  toolCallId: string;
  name: string;
  args: Record<string, unknown>;
  proposedAt: string;
}

interface AgentSnapshot {
  id: string;
  customerId?: string;
  incidentId?: string;
  events: AgentEvent[];
  pending: PendingDestructive | null;
  liveDestructive: boolean;
}

interface AgentResponse {
  sessionId: string;
  status: 'complete' | 'pending_confirmation';
  action?: PendingDestructive;
  snapshot: AgentSnapshot;
}

interface Skill {
  id: string;
  title: string;
  icon?: string;
  category: string;
  description: string;
  promptTemplate: string;
}

interface PlaybookListItem {
  id: string;
  customerId: string;
  /** Human-friendly label saved by the MSSP admin (DB column display_name). */
  displayName: string;
  /** Underlying Logic App workflow name — fallback when displayName is blank. */
  workflowName?: string;
  description?: string | null;
  enabled: boolean;
  triggerType?: string;
  /** For sentinelEntity playbooks: which entity kind it operates on. */
  entityKind?: string | null;
}

type EntityForContext = string | { type?: string; name?: string; verdict?: string; sha256?: string };

interface IncidentForContext {
  id: string;
  incidentNumber?: string;
  title: string;
  severity: string;
  status: string;
  description?: string;
  armIncidentName?: string;
  graphIncidentId?: string;
  webUrl?: string;
}

interface Props {
  incidentId: string;
  incidentNumber?: string;
  customerId?: string;
  /** Plain-text summary of the incident used to seed the agent's system context. */
  incidentSummary: string;
  /** Optional pre-detected primary user UPN (used for {{upn}} skill substitution). */
  primaryUpn?: string | null;
  /** Optional pre-detected primary host (used for {{hostname}} skill substitution). */
  primaryHost?: string | null;
  /** Full incident object — forwarded to the server so playbook tools can build envelopes. */
  incident?: IncidentForContext;
  /** Resolved/enriched entities — forwarded to the server for playbook envelopes. */
  entities?: EntityForContext[];
  /** MITRE tactics for the agent's playbook envelope context. */
  mitreTactics?: string[];
  /** Alert count for envelope context. */
  alertsCount?: number;
}

const TOOL_LABELS: Record<string, string> = {
  run_sentinel_kql: 'Sentinel KQL',
  get_sentinel_incidents: 'List Incidents',
  get_user_info: 'User Info',
  search_xdr_by_host: 'XDR / Host Search',
  get_machine_isolation_status: 'Machine Status',
  isolate_machine: 'Isolate Machine',
  unisolate_machine: 'Release Machine',
  reset_user_password_and_revoke_sessions: 'Reset Password + Revoke',
  list_customer_playbooks: 'List Playbooks',
  run_customer_playbook: 'Run Playbook',
};

function formatToolName(name: string): string {
  return TOOL_LABELS[name] ?? name;
}

function fillTemplate(tpl: string, vars: Record<string, string>): { text: string; missing: string[] } {
  const missing: string[] = [];
  const text = tpl.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const v = vars[key];
    if (!v) { missing.push(key); return `{{${key}}}`; }
    return v;
  });
  return { text, missing };
}

export function InvestigatePanel({ incidentId, incidentNumber, customerId, incidentSummary, primaryUpn, primaryHost, incident, entities, mitreTactics, alertsCount }: Props) {
  // SOC L1 can view playbook *configuration* in Settings, but is not allowed
  // to RUN playbooks during an investigation — that's an L2+ action.
  const { user } = useAuth();
  const canRunPlaybooks = user?.role !== 'soc_l1';
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<AgentSnapshot | null>(null);
  const [pending, setPending] = useState<PendingDestructive | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [skillPromptNeeded, setSkillPromptNeeded] = useState<{ skill: Skill; missing: string[]; values: Record<string, string> } | null>(null);
  const [confirmJustification, setConfirmJustification] = useState('');
  // When the AI circuit breaker is open, the server returns 503 with a
  // retryAfterMs hint. We show a sticky banner with a live countdown and
  // disable the Send/skill buttons until it expires.
  const [circuitOpen, setCircuitOpen] = useState<{ retryAt: number; message: string } | null>(null);
  const [, forceTick] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Drive the circuit-open countdown — repaint every second until expiry.
  useEffect(() => {
    if (!circuitOpen) return;
    const id = window.setInterval(() => {
      if (Date.now() >= circuitOpen.retryAt) {
        setCircuitOpen(null);
      } else {
        forceTick(t => t + 1);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [circuitOpen]);
  // Monotonic counter incremented when the incident changes. In-flight requests
  // capture the current value and discard their response if it has changed by
  // the time they resolve — prevents stale responses for incident A from
  // overwriting state after the user has switched to incident B.
  const requestEpochRef = useRef(0);

  // Reset whenever the incident OR customer changes. Customer is included on
  // purpose: an MSSP analyst can switch tenants while the incidentId happens
  // to repeat across customers (numeric counters), and we must NOT replay an
  // old session against a different tenant — both for correctness and to
  // prevent destructive actions firing in the wrong tenant.
  useEffect(() => {
    requestEpochRef.current += 1;
    setSessionId(null);
    setSnapshot(null);
    setPending(null);
    setInput('');
    setError(null);
    setConfirmJustification('');
    setSkillPromptNeeded(null);
  }, [incidentId, customerId]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ skills: Skill[] }>('/skills')
      .then(r => { if (!cancelled) setSkills(r.skills ?? []); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Per-customer Sentinel Logic-App playbooks. Re-fetch when the customer
  // changes (e.g. MSSP analyst opens an incident in another tenant). Critical:
  // clear the previous tenant's playbooks BEFORE the fetch starts and on any
  // failure, so a slow/failed fetch can't leave a stale tenant's buttons
  // visible/clickable in the new tenant context.
  useEffect(() => {
    setPlaybooks([]);
    if (!customerId) return;
    let cancelled = false;
    apiFetch<{ playbooks: PlaybookListItem[] }>(`/customers/${customerId}/playbooks`)
      .then(r => {
        if (cancelled) return;
        setPlaybooks((r.playbooks ?? []).filter(p => p.enabled));
      })
      .catch(() => {
        if (cancelled) return;
        setPlaybooks([]); // safety: never leak stale tenant data on error
      });
    return () => { cancelled = true; };
  }, [customerId]);

  // Auto-scroll on new events.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [snapshot?.events.length, pending]);

  async function send(text: string) {
    if (!text.trim() || sending) return;
    const epoch = requestEpochRef.current;
    setSending(true);
    setError(null);
    try {
      // Build the incidentContext payload so the server-side agent can fill
      // Sentinel Logic-App envelopes when invoking run_customer_playbook. We
      // only include it when we actually have an incident object — for plain
      // chat without incident context this stays undefined (server tolerates).
      // The server's IncidentRunContext (playbookEnvelope.ts) expects entities
      // nested as `incident.entities`, NOT a top-level `entities` field. Flatten
      // here so buildSentinelEnvelope() can pick them up directly.
      const incidentContext = incident
        ? {
            incident: {
              id: incident.id,
              incidentNumber: incident.incidentNumber,
              title: incident.title,
              severity: incident.severity,
              status: incident.status,
              description: incident.description,
              armIncidentName: incident.armIncidentName,
              graphIncidentId: incident.graphIncidentId,
              url: incident.webUrl,
              mitreTactics: mitreTactics ?? [],
              alertsCount: alertsCount ?? 0,
              entities: (entities ?? []).map(e =>
                typeof e === 'string'
                  ? { name: e }
                  : { type: e.type, name: e.name, sha256: e.sha256, verdict: e.verdict },
              ),
            },
          }
        : undefined;
      const r = await apiFetch<AgentResponse>('/ai-agent/investigate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          customerId,
          incidentId,
          incidentSummary,
          incidentContext,
          message: text,
        }),
      });
      if (epoch !== requestEpochRef.current) return; // user navigated away
      setSessionId(r.sessionId);
      setSnapshot(r.snapshot);
      setPending(r.action ?? r.snapshot.pending ?? null);
      setInput('');
    } catch (e) {
      if (epoch !== requestEpochRef.current) return;
      handleApiError(e);
    } finally {
      if (epoch === requestEpochRef.current) { setSending(false); setActiveActionId(null); }
    }
  }

  /**
   * Map an apiFetch error to UI state. Circuit-open errors (503 with the
   * retryAfterMs hint from the server) drive the sticky banner; everything
   * else goes through the normal red error strip.
   */
  const handleApiError = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.circuitOpen) {
      const retry = e.retryAfterMs ?? 60_000;
      setCircuitOpen({ retryAt: Date.now() + retry, message: e.message });
      setError(null);
      return;
    }
    setError(String(e instanceof Error ? e.message : e));
  }, []);

  async function confirm(approved: boolean) {
    if (!sessionId || !pending || sending) return;
    const epoch = requestEpochRef.current;
    setSending(true);
    setError(null);
    try {
      const r = await apiFetch<AgentResponse>('/ai-agent/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // customerId is required by customerScopeGuard for non-admin roles.
        body: JSON.stringify({ sessionId, customerId, approved, justification: confirmJustification.trim() || undefined }),
      });
      if (epoch !== requestEpochRef.current) return;
      setSnapshot(r.snapshot);
      setPending(r.action ?? r.snapshot.pending ?? null);
      setConfirmJustification('');
    } catch (e) {
      if (epoch !== requestEpochRef.current) return;
      handleApiError(e);
    } finally {
      if (epoch === requestEpochRef.current) setSending(false);
    }
  }

  /**
   * Ask the server to flag the in-flight session for graceful stop. The agent
   * loop checks the flag at its next turn boundary and emits a synthetic
   * "[Investigation stopped by analyst.]" message. Idempotent — safe to
   * spam-click. We refresh the snapshot from the response so the UI shows
   * the stopped event immediately.
   */
  async function stop() {
    if (!sessionId) return;
    try {
      const r = await apiFetch<{ ok: boolean; sessionId: string; snapshot?: AgentSnapshot }>(
        '/ai-agent/stop',
        // customerId is required by customerScopeGuard for non-admin roles.
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId, customerId }) },
      );
      if (r.snapshot) {
        setSnapshot(r.snapshot);
        setPending(r.snapshot.pending ?? null);
      }
    } catch (e) {
      handleApiError(e);
    }
  }

  /**
   * Ask Neo to run a specific Sentinel playbook by name. The agent will call
   * the run_customer_playbook tool, which is destructive and therefore goes
   * through the same red confirmation gate as isolate/reset actions. We don't
   * call the playbook endpoint directly here on purpose — the point is to keep
   * a single audit trail (agent_session_events) and reuse the destructive
   * confirmation UX the analyst is already used to.
   */
  function runPlaybook(pb: PlaybookListItem) {
    const label = pb.displayName?.trim() || pb.workflowName?.trim() || pb.id;
    const desc = pb.description ? ` (${pb.description})` : '';
    const msg = `Please run the Sentinel playbook "${label}" (id: ${pb.id})${desc} on this incident. Use the current incident context for the entity/incident envelope.`;
    setActiveActionId(pb.id);
    void send(msg);
  }

  function runSkill(skill: Skill) {
    const vars: Record<string, string> = {};
    if (primaryUpn) vars["upn"] = primaryUpn;
    if (primaryHost) vars["hostname"] = primaryHost;
    const { text, missing } = fillTemplate(skill.promptTemplate, vars);
    if (missing.length === 0) {
      setActiveActionId(skill.id);
      void send(text);
      return;
    }
    // Need user input for missing variables (e.g. UPN or hostname)
    const initial: Record<string, string> = {};
    for (const k of missing) initial[k] = vars[k] ?? '';
    setSkillPromptNeeded({ skill, missing, values: initial });
  }

  function submitSkillForm() {
    if (!skillPromptNeeded) return;
    const { skill, values } = skillPromptNeeded;
    const { text, missing } = fillTemplate(skill.promptTemplate, values);
    if (missing.length > 0) return; // still missing — keep dialog open
    setSkillPromptNeeded(null);
    setActiveActionId(skill.id);
    void send(text);
  }

  const events = snapshot?.events ?? [];

  const grouped = useMemo(() => {
    const cats = new Map<string, Skill[]>();
    for (const s of skills) {
      const arr = cats.get(s.category) ?? [];
      arr.push(s);
      cats.set(s.category, arr);
    }
    return Array.from(cats.entries());
  }, [skills]);

  return (
    <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-lg p-3 mb-4" aria-label="Agentic investigation">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold">Agentic Investigation</p>
          {incidentNumber && (
            <span className="text-[10px] text-muted-foreground">incident #{incidentNumber}</span>
          )}
          {sending && (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/50 px-2 py-0.5 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
              Running…
            </span>
          )}
        </div>
        {sessionId && (
          <code className="text-[10px] text-muted-foreground bg-card px-1.5 py-0.5 rounded border border-border" title={sessionId}>Agent KloudSecure</code>
        )}
      </div>

      {/* Skills row */}
      {skills.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {grouped.map(([cat, list]) => (
            <div key={cat} className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold w-16 shrink-0">{cat}</span>
              {list.map(s => {
                const isActive = sending && activeActionId === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => runSkill(s)}
                    disabled={sending || !!pending}
                    title={isActive ? 'Investigation running…' : s.description}
                    className={`text-[11px] border px-2 py-1 rounded font-medium transition-all disabled:cursor-not-allowed ${
                      isActive
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm shadow-emerald-500/40 animate-pulse'
                        : 'bg-card hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border-emerald-300 dark:border-emerald-800 text-foreground disabled:opacity-40'
                    }`}
                  >
                    {isActive && <span className="inline-block mr-1 animate-spin" aria-hidden>⟳</span>}
                    {s.title}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Per-customer Sentinel playbooks. Distinct sky-blue styling so analysts
          can immediately tell a Logic-App playbook from a built-in skill.
          Hidden entirely for SOC L1 — running playbooks is an L2+ action. */}
      {canRunPlaybooks && playbooks.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold w-16 shrink-0">Playbooks</span>
            {playbooks.map(pb => {
              const label = pb.displayName?.trim() || pb.workflowName?.trim() || pb.id;
              const isActive = sending && activeActionId === pb.id;
              return (
                <button
                  key={pb.id}
                  onClick={() => runPlaybook(pb)}
                  disabled={sending || !!pending}
                  title={isActive ? 'Investigation running…' : (pb.description ?? `Run Sentinel playbook ${label}`)}
                  className={`text-[11px] border px-2 py-1 rounded font-medium transition-all disabled:cursor-not-allowed ${
                    isActive
                      ? 'bg-sky-600 border-sky-600 text-white shadow-sm shadow-sky-500/40 animate-pulse'
                      : 'bg-card hover:bg-sky-100 dark:hover:bg-sky-900/50 border-sky-300 dark:border-sky-800 text-foreground disabled:opacity-40'
                  }`}
                >
                  {isActive ? <span className="inline-block mr-1 animate-spin" aria-hidden>⟳</span> : '▶ '}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Circuit-open banner — server is fast-failing AI calls */}
      {circuitOpen && (
        <div className="mb-3 bg-amber-50 dark:bg-amber-950/40 border-2 border-amber-400 dark:border-amber-700 rounded-lg p-3 flex items-start gap-3">
          <span className="inline-block w-2.5 h-2.5 mt-1.5 rounded-full bg-amber-500 shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-200">
              AI service paused by circuit breaker
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">{circuitOpen.message}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Auto-retry available in <span className="font-mono font-semibold">{Math.max(0, Math.ceil((circuitOpen.retryAt - Date.now()) / 1000))}s</span>
            </p>
          </div>
          <button
            onClick={() => setCircuitOpen(null)}
            className="text-[11px] text-amber-700 dark:text-amber-300 hover:underline shrink-0"
            aria-label="Dismiss circuit-breaker banner"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Conversation */}
      {events.length > 0 && (
        <div ref={scrollRef} className="bg-card border border-border rounded-lg p-2 max-h-[420px] overflow-y-auto mb-3 space-y-2">
          {events.map((ev, i) => <EventRow key={i} ev={ev} />)}
        </div>
      )}

      {/* Pending confirmation gate */}
      {pending && (
        <div className="bg-red-50 dark:bg-red-950/40 border-2 border-red-400 dark:border-red-700 rounded-lg p-3 mb-3">
          <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1">Destructive action requires confirmation</p>
          <p className="text-xs text-foreground mb-2">
            The agent wants to run <code className="bg-card px-1 rounded">{formatToolName(pending.name)}</code> with:
          </p>
          <pre className="text-[11px] bg-card border border-border rounded p-2 overflow-x-auto mb-2 whitespace-pre-wrap break-words">
{JSON.stringify(pending.args, null, 2)}
          </pre>
          <textarea
            value={confirmJustification}
            onChange={e => setConfirmJustification(e.target.value)}
            placeholder="Optional analyst justification (audit-logged)…"
            rows={2}
            disabled={sending}
            className="w-full text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-red-500 resize-none mb-2"
          />
          <div className="flex gap-2">
            <button
              onClick={() => confirm(true)}
              disabled={sending}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded font-bold text-sm transition-colors disabled:opacity-50"
            >
              {sending ? 'Executing…' : 'Approve & Execute'}
            </button>
            <button
              onClick={() => confirm(false)}
              disabled={sending}
              className="flex-1 bg-card hover:bg-muted border border-border text-foreground px-3 py-2 rounded font-semibold text-sm transition-colors disabled:opacity-50"
            >
              Deny
            </button>
          </div>
        </div>
      )}

      {/* Skill variable prompt */}
      {skillPromptNeeded && (
        <div className="bg-card border border-border rounded-lg p-3 mb-3">
          <p className="text-xs font-semibold text-foreground mb-2">"{skillPromptNeeded.skill.title}" needs:</p>
          <div className="space-y-2">
            {skillPromptNeeded.missing.map(field => (
              <div key={field}>
                <label className="block text-[11px] text-muted-foreground mb-0.5 uppercase tracking-wide">{field}</label>
                <input
                  type="text"
                  value={skillPromptNeeded.values[field] ?? ''}
                  onChange={e => setSkillPromptNeeded(prev => prev ? { ...prev, values: { ...prev.values, [field]: e.target.value } } : prev)}
                  placeholder={field === 'upn' ? 'user@contoso.com' : field === 'hostname' ? 'WORKSTATION-042' : ''}
                  className="w-full text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={submitSkillForm}
              disabled={skillPromptNeeded.missing.some(f => !skillPromptNeeded.values[f]?.trim())}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold text-sm transition-colors disabled:opacity-50"
            >
              Run Playbook
            </button>
            <button
              onClick={() => setSkillPromptNeeded(null)}
              className="bg-card hover:bg-muted border border-border text-foreground px-3 py-1.5 rounded text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-2 py-1.5 mb-2 whitespace-pre-wrap break-words font-mono">
          {error}
        </div>
      )}

      {/* Free-form input */}
      <div className="flex gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(input); }
          }}
          placeholder={pending ? 'Confirm or deny the pending action above first…' : 'Ask the agent to investigate (Cmd/Ctrl+Enter to send)…'}
          rows={2}
          disabled={sending || !!pending || !!circuitOpen}
          className="flex-1 text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
        />
        {sending && !pending && sessionId ? (
          // Stop button — graceful interrupt while a turn is in flight. Hidden
          // when a destructive confirmation is pending (the analyst already
          // has Approve/Deny). Hidden if there's no session yet.
          <button
            onClick={() => void stop()}
            className="self-stretch bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded font-semibold text-sm transition-colors"
            title="Stop the in-flight investigation at the next safe boundary"
          >
            ■ Stop
          </button>
        ) : (
          <button
            onClick={() => void send(input)}
            disabled={sending || !input.trim() || !!pending || !!circuitOpen}
            className="self-stretch bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold text-sm transition-colors disabled:opacity-50"
          >
            {sending ? '…' : 'Send'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Render an assistant turn as Markdown. Code blocks (especially KQL) get a
 * dark monospace block; tables, lists, and inline code follow our theme.
 * Unknown tags are stripped by react-markdown by default — no raw HTML.
 */
function AssistantMarkdown({ text }: { text: string }) {
  return (
    <div className="text-xs text-foreground prose prose-sm dark:prose-invert max-w-none
                    prose-p:my-1 prose-headings:my-1.5 prose-headings:font-bold
                    prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
                    prose-code:before:content-none prose-code:after:content-none
                    prose-code:bg-card prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[11px]
                    prose-pre:bg-card prose-pre:border prose-pre:border-border prose-pre:rounded prose-pre:p-2 prose-pre:my-1.5 prose-pre:text-[11px]
                    prose-table:text-[11px] prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1
                    prose-blockquote:border-l-emerald-500 prose-blockquote:text-muted-foreground
                    prose-a:text-emerald-600 dark:prose-a:text-emerald-400">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable (insecure context, etc.) — silently no-op */
        }
      }}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-card hover:bg-muted text-muted-foreground transition-colors shrink-0"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function EventRow({ ev }: { ev: AgentEvent }) {
  if (ev.kind === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs max-w-[85%] whitespace-pre-wrap break-words">
          {ev.text}
        </div>
      </div>
    );
  }
  if (ev.kind === 'assistant') {
    return (
      <div className="bg-muted/50 rounded-lg px-3 py-2 group relative">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="text-[9px] uppercase font-bold tracking-wide text-emerald-700 dark:text-emerald-400">Agent</span>
          <CopyButton text={ev.text} />
        </div>
        <AssistantMarkdown text={ev.text} />
      </div>
    );
  }
  if (ev.kind === 'stopped') {
    return (
      <div className="text-[11px] bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded px-2 py-1.5 italic">
        <span className="font-bold text-amber-700 dark:text-amber-300">Stopped</span>
        <span className="text-foreground">: {ev.reason}</span>
      </div>
    );
  }
  if (ev.kind === 'tool_call') {
    return (
      <div className={`text-[11px] border rounded px-2 py-1.5 ${
        ev.destructive
          ? 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800'
          : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900'
      }`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] uppercase font-bold tracking-wide px-1 py-0.5 rounded ${
            ev.destructive ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-100' : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-100'
          }`}>
            {ev.destructive ? 'Destructive' : 'Tool'}
          </span>
          <span className="font-semibold text-foreground">{formatToolName(ev.name)}</span>
        </div>
        <pre className="mt-1 font-mono text-[10px] text-muted-foreground whitespace-pre-wrap break-words">{JSON.stringify(ev.args, null, 2)}</pre>
      </div>
    );
  }
  if (ev.kind === 'tool_result') {
    return (
      <div className="text-[10px] bg-card border border-border rounded px-2 py-1.5 font-mono text-muted-foreground">
        <div className="text-[9px] uppercase font-bold tracking-wide text-emerald-600 dark:text-emerald-400 mb-0.5">Result · {formatToolName(ev.name)}</div>
        <pre className="whitespace-pre-wrap break-words">{ev.resultPreview}</pre>
      </div>
    );
  }
  if (ev.kind === 'denied') {
    return (
      <div className="text-[11px] bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded px-2 py-1.5">
        <span className="font-bold text-amber-700 dark:text-amber-300">Analyst denied</span>{' '}
        <span className="text-foreground">{formatToolName(ev.name)}</span>
        {ev.reason && <p className="text-muted-foreground italic mt-0.5">"{ev.reason}"</p>}
      </div>
    );
  }
  if (ev.kind === 'sentinel_comment') {
    // Compact, single-line confirmation row. Posted = sky tint, skipped =
    // muted (no incident bound, etc.), failed = amber so it's noticed.
    const tone =
      ev.status === 'posted'
        ? 'bg-sky-50 dark:bg-sky-950/40 border-sky-300 dark:border-sky-800 text-sky-800 dark:text-sky-200'
        : ev.status === 'failed'
        ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
        : 'bg-muted/40 border-border text-muted-foreground';
    const label =
      ev.status === 'posted'
        ? `Posted to Sentinel incident comments${ev.method ? ` · via ${ev.method.toUpperCase()}` : ''}`
        : ev.status === 'failed'
        ? `Failed to post to Sentinel comments${ev.reason ? `: ${ev.reason}` : ''}`
        : `Not posted to Sentinel${ev.reason ? `: ${ev.reason}` : ''}`;
    return (
      <div className={`text-[10px] border rounded px-2 py-1 italic ${tone}`}>
        <span className="font-bold uppercase tracking-wide text-[9px] mr-1">Sentinel</span>
        {label}
      </div>
    );
  }
  return null;
}
